import { formatMinor, type ChartAccount, type GifiRow } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Input, PageHeader, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { chartQuery, gifiQuery, setAccountGifi } from '../../shared/api/ledger-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { gifiCsv } from './books-format.js';
import { AdjustmentsCard } from './adjustments-card.js';
import { OpeningBalancesCard } from './opening-balances-card.js';
import { YearEndCard } from './year-end-card.js';

/**
 * The books' own setup and year-end: where they start, how each account is
 * filed with the CRA, and the T2's financial statements ready for the
 * accountant's tax software.
 */
export function BooksPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const membership = session.organizations.find(
    (entry) => entry.organization.id === organizationId,
  );
  const canEdit = membership?.permissions.includes('transaction:update') ?? false;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Books"
        title="Books setup and year-end"
        description="Where the books start, how each account is filed, and the T2 statements your accountant imports."
      />
      <OpeningBalancesCard organizationId={organizationId} canEdit={canEdit} />
      <AdjustmentsCard organizationId={organizationId} canEdit={canEdit} />
      <YearEndCard organizationId={organizationId} canEdit={canEdit} />
      <T2Card organizationId={organizationId} />
      <GifiCodesCard organizationId={organizationId} canEdit={canEdit} />
    </div>
  );
}

const SCHEDULE_TITLES: Readonly<Record<GifiRow['schedule'], string>> = {
  '100': 'Schedule 100: balance sheet',
  '125': 'Schedule 125: income statement',
};

function T2Card({ organizationId }: { organizationId: string }) {
  const lastYear = new Date().getFullYear() - 1;
  const [from, setFrom] = useState(`${String(lastYear)}-01-01`);
  const [to, setTo] = useState(`${String(lastYear)}-12-31`);
  const report = useQuery(gifiQuery(organizationId, from, to));
  const rows = report.data ?? [];
  const unfiled = rows.filter((row) => row.gifiCode === null && row.amountMinor !== '0');
  const amount = (code: string) => rows.find((row) => row.gifiCode === code)?.amountMinor;
  const balances = amount('2599') !== undefined && amount('2599') === amount('3640');

  const download = () => {
    const blob = new Blob([gifiCsv(rows)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gifi-${from}-to-${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader
        title="T2 financial statements (GIFI)"
        description={
          report.data === undefined
            ? 'Reading the ledger…'
            : unfiled.length > 0
              ? `${String(unfiled.length)} balance${unfiled.length === 1 ? ' has' : 's have'} no GIFI code and cannot be filed as ${unfiled.length === 1 ? 'it is' : 'they are'}: ${unfiled.map((row) => row.accounts ?? '').join('; ')}. Clear or map ${unfiled.length === 1 ? 'it' : 'them'} first.`
              : balances
                ? 'Total assets equal total liabilities and equity. Ready for the accountant.'
                : 'Schedule 100 does not balance yet.'
        }
      />
      <CardBody className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-content-muted">Fiscal year</span>
          <input
            type="date"
            value={from}
            onChange={(event) => {
              if (event.target.value !== '') setFrom(event.target.value);
            }}
            className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1"
          />
          <span className="text-content-muted">to</span>
          <input
            type="date"
            value={to}
            onChange={(event) => {
              if (event.target.value !== '') setTo(event.target.value);
            }}
            className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1"
          />
          <Button size="sm" variant="secondary" onClick={download} disabled={rows.length === 0}>
            Download for tax software (CSV)
          </Button>
        </div>
        {report.isPending ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {(['100', '125'] as const).map((schedule) => (
              <table key={schedule} className="w-full self-start text-sm tabular">
                <thead>
                  <tr>
                    <th
                      colSpan={2}
                      className="pb-1 text-left text-xs font-medium uppercase text-content-muted"
                    >
                      {SCHEDULE_TITLES[schedule]}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows
                    .filter((row) => row.schedule === schedule)
                    .map((row) => (
                      <tr
                        key={`${row.gifiCode ?? 'none'}-${row.accounts ?? ''}`}
                        className={row.isTotal ? 'border-t border-border font-medium' : ''}
                      >
                        <td className="py-0.5">
                          <span
                            className={
                              row.gifiCode === null ? 'text-warning' : 'text-content-muted'
                            }
                          >
                            {row.gifiCode ?? 'No code'}
                          </span>{' '}
                          <span className="text-content-muted">{row.accounts ?? ''}</span>
                        </td>
                        <td className="py-0.5 text-right">{formatMinor(row.amountMinor)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ))}
          </div>
        )}
        <p className="text-xs text-content-muted">
          Meals are shown in full (item 8523); the 50% limit is applied on Schedule 1, with the
          other tax adjustments, by the accountant. Pathara prepares the statements; the T2 itself
          is filed through CRA-certified tax software.
        </p>
      </CardBody>
    </Card>
  );
}

function GifiCodesCard({ organizationId, canEdit }: { organizationId: string; canEdit: boolean }) {
  const chart = useQuery(chartQuery(organizationId));
  const accounts = chart.data ?? [];
  const missing = accounts.filter((account) => account.gifiCode === null);
  return (
    <Card>
      <CardHeader
        title="How each account is filed"
        description={
          chart.data === undefined
            ? 'Reading…'
            : `Every account carries the CRA GIFI code it is reported under. ${String(accounts.length - missing.length)} of ${String(accounts.length)} have one; those without are holding accounts whose balance must be cleared, not filed.`
        }
      />
      <CardBody>
        {chart.isPending ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <table className="w-full text-sm tabular">
            <thead>
              <tr className="text-left text-xs text-content-muted">
                <th className="py-1 font-medium">Account</th>
                <th className="w-28 py-1 font-medium">GIFI</th>
                <th className="w-28 py-1 font-medium">If reversed</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <GifiRowEditor
                  key={account.id}
                  organizationId={organizationId}
                  account={account}
                  canEdit={canEdit}
                />
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function GifiRowEditor({
  organizationId,
  account,
  canEdit,
}: {
  organizationId: string;
  account: ChartAccount;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState(account.gifiCode ?? '');
  const [reversed, setReversed] = useState(account.gifiCodeIfReversed ?? '');
  const [error, setError] = useState<string | null>(null);
  const valid = /^\d{4}$/.test(code) && (reversed === '' || /^\d{4}$/.test(reversed));
  const changed =
    code !== (account.gifiCode ?? '') || reversed !== (account.gifiCodeIfReversed ?? '');

  const save = useMutation({
    mutationFn: () =>
      setAccountGifi(organizationId, account.id, {
        gifiCode: code,
        gifiCodeIfReversed: reversed === '' ? null : reversed,
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['ledger', organizationId, 'chart'] });
      void queryClient.invalidateQueries({ queryKey: ['reports', organizationId, 'gifi'] });
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'Not saved.'),
  });

  const commit = () => {
    if (changed && valid) save.mutate();
  };

  return (
    <tr>
      <td className="py-0.5">
        <span className="text-content-muted">{account.code}</span> {account.name}
        {error === null ? null : <Alert tone="danger">{error}</Alert>}
      </td>
      <td className="py-0.5 pr-2">
        {canEdit ? (
          <Input
            aria-label={`GIFI code for ${account.name}`}
            inputMode="numeric"
            maxLength={4}
            value={code}
            placeholder="none"
            hasError={code !== '' && !/^\d{4}$/.test(code)}
            onChange={(event) => setCode(event.target.value.trim())}
            onBlur={commit}
          />
        ) : (
          (account.gifiCode ?? 'none')
        )}
      </td>
      <td className="py-0.5">
        {canEdit ? (
          <Input
            aria-label={`GIFI code for ${account.name} when its balance reverses`}
            inputMode="numeric"
            maxLength={4}
            value={reversed}
            hasError={reversed !== '' && !/^\d{4}$/.test(reversed)}
            onChange={(event) => setReversed(event.target.value.trim())}
            onBlur={commit}
          />
        ) : (
          (account.gifiCodeIfReversed ?? '')
        )}
      </td>
    </tr>
  );
}
